/**
 * Agenda calendar (design v2.1): week and day views like Apple Calendar — an hour grid, one column
 * per day, the appointments as coloured blocks (one colour per appointment type, always with its
 * name written), a red line for the current time. Times are read in Africa/Douala.
 */

import type { Appointment } from "@agencyos/api-client";
import type { Tone } from "@agencyos/ui-web";
import { Phone, Sparkles, Store } from "lucide-react";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { localized } from "../../components/TranslatedFields";

export const TZ = "Africa/Douala";
const HOUR_PX = 64;
/** Colour of each appointment type, by its order in the list of types (never meaning on its own). */
export const TYPE_TONES: Tone[] = ["accent", "success", "warning", "info", "red", "neutral"];

/** YYYY-MM-DD of an instant in Douala. */
export function dayKey(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(date);
}

/** Minutes since midnight (Douala) of an ISO instant. */
function minutesOf(iso: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get("hour") * 60 + get("minute");
}

/** Monday of the week of a YYYY-MM-DD day. */
export function mondayOf(day: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  const offset = (date.getUTCDay() + 6) % 7;
  return addDays(day, -offset);
}

export function addDays(day: string, n: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + n);
  return date.toISOString().slice(0, 10);
}

/** Visible hours: the agency's opening hours when known, widened to fit every appointment. */
export function hourRange(items: readonly Appointment[], openingHours: unknown): [number, number] {
  let first = 8;
  let last = 18;
  if (openingHours && typeof openingHours === "object") {
    const times = Object.values(openingHours as Record<string, unknown>)
      .flatMap((value) => String(value ?? "").match(/\d{1,2}:\d{2}/g) ?? [])
      .map((text) => Number(text.split(":")[0]) + Number(text.split(":")[1]) / 60);
    if (times.length) {
      first = Math.floor(Math.min(...times));
      last = Math.ceil(Math.max(...times));
    }
  }
  for (const a of items) {
    first = Math.min(first, Math.floor(minutesOf(a.starts_at) / 60));
    last = Math.max(last, Math.ceil(minutesOf(a.ends_at) / 60));
  }
  return [Math.max(0, first), Math.min(24, Math.max(last, first + 4))];
}

export function Calendar({
  days,
  items,
  typeOrder,
  openingHours,
  onOpen,
}: {
  days: string[];
  items: readonly Appointment[];
  typeOrder: readonly string[];
  openingHours: unknown;
  onOpen: (appointment: Appointment) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  const [first, last] = hourRange(items, openingHours);
  const hours = Array.from({ length: last - first }, (_, i) => first + i);
  const today = dayKey(new Date());
  const now = minutesOf(new Date().toISOString());
  const nowTop = ((now - first * 60) / 60) * HOUR_PX;
  const time = (iso: string) => new Date(iso).toLocaleTimeString(locale, { timeZone: TZ, hour: "2-digit", minute: "2-digit" });
  const single = days.length === 1;

  return (
    <div className={`cal${single ? " cal--day" : ""}`} style={{ "--cal-days": days.length, "--cal-hour": `${HOUR_PX}px` } as CSSProperties}>
      <div className="cal__head" aria-hidden="true">
        <span />
        {days.map((day) => {
          const date = new Date(`${day}T12:00:00Z`);
          return (
            <span key={day} className={`cal__day-name${day === today ? " cal__day-name--today" : ""}`}>
              <span>{date.toLocaleDateString(locale, { weekday: single ? "long" : "short", timeZone: "UTC" })}</span>
              <strong className="ao-num">{date.toLocaleDateString(locale, { day: "numeric", timeZone: "UTC" })}</strong>
            </span>
          );
        })}
      </div>
      <div className="cal__body" style={{ height: hours.length * HOUR_PX }}>
        <div className="cal__hours" aria-hidden="true">
          {hours.map((h) => (
            <span key={h} className="cal__hour ao-num">
              {String(h).padStart(2, "0")}:00
            </span>
          ))}
        </div>
        {days.map((day) => {
          const list = items.filter((a) => dayKey(new Date(a.starts_at)) === day);
          const date = new Date(`${day}T12:00:00Z`);
          return (
            <section
              key={day}
              className={`cal__col${day === today ? " cal__col--today" : ""}`}
              aria-label={`${date.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })} · ${t("agenda.count", { count: list.length })}`}
            >
              {day === today && nowTop >= 0 && nowTop <= hours.length * HOUR_PX ? (
                <span className="cal__now" style={{ top: nowTop }} aria-hidden="true" />
              ) : null}
              {list.map((a) => {
                const start = minutesOf(a.starts_at);
                const end = Math.max(minutesOf(a.ends_at), start + 20);
                const index = Math.max(0, typeOrder.indexOf(a.type));
                const tone = TYPE_TONES[index % TYPE_TONES.length] ?? "accent";
                const short = end - start < 35;
                const long = end - start >= 60;
                return (
                  <button
                    key={a.id}
                    type="button"
                    className={`cal__event ao-tone--${tone}${short ? " cal__event--short" : ""}${a.status === "cancelled" || a.status === "no_show" ? " cal__event--off" : ""}`}
                    style={{ top: ((start - first * 60) / 60) * HOUR_PX + 1, height: ((end - start) / 60) * HOUR_PX - 3 }}
                    onClick={() => onOpen(a)}
                  >
                    <span className="cal__event-time ao-num">
                      {time(a.starts_at)}
                      {a.mode === "call" ? <Phone size={11} strokeWidth={2} aria-hidden="true" /> : <Store size={11} strokeWidth={2} aria-hidden="true" />}
                      {a.booked_by_ai ? <Sparkles size={11} strokeWidth={2} aria-label={t("agenda.byAi")} /> : null}
                    </span>
                    <strong className="cal__event-name">{a.contact_name || a.contact_phone}</strong>
                    {long || single ? <span className="cal__event-type">{localized(a.type_name, i18n.language)}</span> : null}
                  </button>
                );
              })}
            </section>
          );
        })}
      </div>
    </div>
  );
}
